import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // Initialize user from localStorage immediately to avoid flash
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('swisstime_user');
        const token = localStorage.getItem('swisstime_token');
        if (savedUser && token) {
            try {
                return JSON.parse(savedUser);
            } catch {
                return null;
            }
        }
        return null;
    });
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('swisstime_token');
        localStorage.removeItem('swisstime_user');
        setUser(null);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('swisstime_token');
        const savedUser = localStorage.getItem('swisstime_user');
        
        if (token && savedUser) {
            // Verify token is still valid in background
            authAPI.getMe()
                .then(response => {
                    setUser(response.data);
                    localStorage.setItem('swisstime_user', JSON.stringify(response.data));
                })
                .catch(() => {
                    logout();
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [logout]);

    const login = async (email, password) => {
        const response = await authAPI.login({ email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('swisstime_token', access_token);
        localStorage.setItem('swisstime_user', JSON.stringify(userData));
        setUser(userData);
        
        return userData;
    };

    const register = async (name, email, password) => {
        const response = await authAPI.register({ name, email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('swisstime_token', access_token);
        localStorage.setItem('swisstime_user', JSON.stringify(userData));
        setUser(userData);
        
        return userData;
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
